import { useUrlSlug } from 'Utilities/hooks/useUrlSlug';
import {
  FINANCE_PAYMENT_DETAILS_ROUTE,
  FINANCE_PAYMENT_DETAILS_ROUTE_MORE_FUNDS_REQUESTED,
} from 'Utilities/routing';

import { APIResponseStatus } from 'Services/api.types';

import { useServiceFeeEnabled } from 'Components/transactionFees/hooks/useServiceFeeEnabled';
import { useServiceFeeNotice } from 'Components/transactionFees/hooks/useServiceFeeNotice';

import { useSaleDetailsSub } from '../../hooks/useSaleDetailsSub';
import { useVehicleCollectionSub } from '../../hooks/useVehicleCollectionSub';

import { type SaleDetailsContentViewProps, SaleDetailsContentView } from './components/SaleDetailsContentView/SaleDetailsContentView';
import { SaleDetailsContentProps } from './SaleDetails.types';

const FINANCE_PAYMENT_DETAILS_SLUGS: ReadonlySet<string> = new Set([
  FINANCE_PAYMENT_DETAILS_ROUTE.slice(1),
  FINANCE_PAYMENT_DETAILS_ROUTE_MORE_FUNDS_REQUESTED.slice(1),
]);

export const useSaleDetailsContent = ({
  showDetailsItems, title, titleIcon, variant,
}: SaleDetailsContentProps): SaleDetailsContentViewProps => {
  const { isSaleComplete } = useVehicleCollectionSub();
  const slug = useUrlSlug();
  const isFinancePaymentDetailsRoute = FINANCE_PAYMENT_DETAILS_SLUGS.has(slug);

  const {
    enquiryId,
    financingAmount,
    hasNegativeEquity,
    neBankTransferTotalAmount,
    offerStatus,
    sellerAmount,
    serviceFee,
    serviceFeeTiers,
    soldForFigure,
    vehicleThumbnail,
    vrm,
  } = useSaleDetailsSub();

  const { isServiceFeeEnabled } = useServiceFeeEnabled();

  const shouldShowServiceFee = isServiceFeeEnabled && !isFinancePaymentDetailsRoute;

  const { feeAmountGBP } = useServiceFeeNotice({
    enquiryId,
    isServiceFeeEnabled: shouldShowServiceFee,
    serviceFee,
    soldForFigure: soldForFigure ?? null,
    vrm,
  });

  const {
    showHomeCollectionLineItem = true,
    showPayoutDetails = false,
  } = showDetailsItems ?? {};

  return {
    bankTransferFigure: neBankTransferTotalAmount,
    financeFigure: financingAmount,
    isServiceFeeEnabled: shouldShowServiceFee,
    mode: (() => {
      if (isSaleComplete) {
        return 'saleComplete';
      }

      if (hasNegativeEquity) {
        return 'negativeEquity';
      }

      return 'positiveEquity';
    })(),
    sellerFigure: sellerAmount,
    serviceFeeNoticeProps: {
      feeAmountGBP,
      serviceFeeTiers,
    },
    showAsError: offerStatus === APIResponseStatus.ERROR,
    showHomeCollectionLineItem,
    showOverpaymentMessage: !isSaleComplete,
    showPayoutDetails: showPayoutDetails || shouldShowServiceFee,
    showVehicleCard: Boolean(vehicleThumbnail),
    soldForFigure,
    title,
    titleIcon,
    variant,
  };
};

export const SaleDetailsContent = (props: SaleDetailsContentProps) => (
  <SaleDetailsContentView {...useSaleDetailsContent(props)} />
);
